import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { applyBackground, usePrefs } from '../lib/prefs.js';
import { ProfileCard } from './ProfileCard.js';

const GearIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.01a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.01a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1.03z" />
  </svg>
);

export function StatusBar({ onOpenSettings, mobile = false }: { onOpenSettings: () => void; mobile?: boolean }) {
  const { user } = useAuth();
  const [prefs] = usePrefs();
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    applyBackground(prefs.background); // sets gradient, accent vars, ambient classes
  }, [prefs.background]);

  const initial = (user?.name ?? user?.email ?? '?').charAt(0).toUpperCase();

  // ── Mobile: compact cluster pinned to the top-right, inside the header row ──
  if (mobile) {
    return (
      <>
        <div className="fixed top-0 right-2 z-[60] pt-safe">
          <div className="flex items-center gap-1.5 h-12">
            <button onClick={() => setProfileOpen(true)} title="Profile" aria-label="Profile"
              className="w-8 h-8 rounded-full grid place-items-center font-serif italic text-[15px] text-ink bg-gradient-to-br from-amber/60 to-amber/20">
              {initial}
            </button>
            <button onClick={onOpenSettings} title="Settings" aria-label="Settings"
              className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/60 active:text-particle">
              <GearIcon size={16} />
            </button>
          </div>
        </div>
        <ProfileCard open={profileOpen} onClose={() => setProfileOpen(false)} onOpenSettings={onOpenSettings} />
      </>
    );
  }

  // ── Desktop: floating dock bottom-left ──
  return (
    <>
      <div className="fixed bottom-5 left-5 z-[60] flex items-center gap-2 px-2.5 py-2 bg-void/85 border border-ink/15 rounded-full backdrop-blur">
        {/* Name → opens the Profile & Settings card */}
        <button onClick={() => setProfileOpen(true)} title="Profile & Settings"
          className="flex items-center gap-3 pl-1 pr-3 rounded-full hover:bg-white/[0.05] transition-colors">
          <div className="w-10 h-10 rounded-full grid place-items-center font-serif italic text-lg text-ink bg-gradient-to-br from-amber/60 to-amber/20">
            {initial}
          </div>
          <div className="flex flex-col text-left">
            <span className="font-serif text-[16px] text-ink leading-tight">{user?.name ?? user?.email ?? 'signed out'}</span>
            <span className="text-[11px] text-ink/40 flex gap-1.5 items-center">
              <span className="px-1.5 py-px rounded-full border border-ink/15 text-particle">{user?.role ?? '—'}</span>
              <span className="text-ink/25">·</span>
              <span>{user?.tier?.replace('_', ' ') ?? '—'}</span>
            </span>
          </div>
        </button>

        {/* Gear → full settings panel */}
        <button onClick={onOpenSettings} title="Settings"
          className="w-10 h-10 rounded-full border border-ink/10 grid place-items-center text-ink/55 hover:text-particle hover:border-ink/30 transition-all hover:rotate-45">
          <GearIcon />
        </button>
      </div>

      <ProfileCard open={profileOpen} onClose={() => setProfileOpen(false)} onOpenSettings={onOpenSettings} />
    </>
  );
}
